import * as React from "react";
import {Types}    from "@Common/types";
import {Fragment} from "react";
import {cx}       from "emotion";

import "./line-tiles.scss";

import {cs}         from "@Common/react/chain-services";
import {Load}       from "@Common/react/load";
import {UseState}   from "@Common/react/use-state";
import {Responsive} from "@Common/ui-components/responsive";
import {toGroups}   from "@Common/utils/collections";
import {equalDeep}  from "@Common/utils/objects";

import {CssTransition}  from "../../components/css-transition";
import {DelayRender}    from "../../components/delay-render";
import {ShowTransition} from "../../components/show-transition";
import {WidthByWindow}  from "../../components/width-by-window";
import {viewModes}      from "../view-config/view-modes";

import {ViewMoreLineScroller} from "./view-more-line-scroller";

export const rLineTiles = <T,>({
    items,
    viewMode,
    renderItem,
    getViewMoreItems,
    renderViewMoreItem
}:{
    items: T[],
    viewMode: string,
    renderItem: (value: {item: T, viewMore: Types.Common.React.UseState.UseStateNext<boolean>}) => JSX.Element,
    getViewMoreItems: (index : number) => any[],
    renderViewMoreItem: (child: any, index: number) => JSX.Element,
}) => cs(
    ["viewMore", (_, next) => <UseState {...{next,}}/>],
    ["sizes", (_, next) => cs(
        // @ts-ignore
        ["responsive", (_, next) => <Responsive {...{
            sizes: {
                md: 1024,
                lg: 1920,
            },
            next,
        }}/>],
        ["width", (_, next) => <WidthByWindow {...{
            getInitWidth: () => window.innerWidth,
            translate: (width) => {
                const usableWidth = width;
                // const usableWidth = document.documentElement.scrollHeight <= window.innerHeight ? (
                //     width - 60
                // ) : (
                //     width
                // );
                return Math.min(1840, usableWidth);
            },
            next,
        }}/>],
        ({width, responsive}) => {
            const tilesPerRow = Math.floor(width / getTileWidth(viewMode, responsive.ge("lg")));
            const padding = responsive.lt("md") ? 10 : 30;
            const totalPadding = padding * (tilesPerRow - 1);
            const boxWidth = Math.floor((width - totalPadding) / tilesPerRow);
            const boxHeight = boxWidth * 1.15;
            const container = document.querySelector('.line-tiles-3deu');
            if (container) {
                container.style.setProperty('--boxWidth', `${boxWidth}px`);
                container.style.setProperty('--boxHeight', `${boxHeight}px`);
                container.style.setProperty('--padding', `${padding}px`);
            }
            return next({
                tilesPerRow,
                padding,
                boxWidth,
                boxHeight,
            });
        },
    )],
    ({viewMore, sizes: {tilesPerRow, padding, boxWidth, boxHeight}}) => (
        <div className="line-tiles-3deu">
            {toGroups(items, tilesPerRow).map((line, li) => (
                <Fragment key={li}>
                    <div className="line">
                        {line.map((item, ii) => (
                            <div
                                className="box" key={ii}
                                style={{
                                    width: boxWidth,
                                    height: boxHeight,
                                    marginLeft: ii && padding,
                                }}
                            >
                                {renderItem({
                                    item,
                                    viewMore: {
                                        value: equalDeep(viewMore.value, [li, ii]),
                                        onChange: (selected) => viewMore.onChange(selected ? [li, ii] : null),
                                    },
                                })}
                            </div>
                        ))}
                    </div>

                    {cs(
                        ["transition", (_, next) => <ShowTransition {...{
                            show: viewMore.value && viewMore.value[0] === li,
                            retain: viewMore.value,
                            next,
                        }}/>],
                        ({transition}) => (
                            <Fragment>
                                <div className={cx("view-more-arrow", transition.state)}>
                                    <div
                                        className="arrow"
                                        style={{
                                            left: boxWidth/2 - 19 + transition.retain[1]*(boxWidth + padding),
                                        }}
                                    />
                                </div>

                                <div className={cx("view-more", transition.state, viewMode)}>
                                    {cs(
                                        (_, next) => <ViewMoreLineScroller {...{next}}/>,
                                        ({}) => (
                                            <div
                                                className="boxes"
                                                key={transition.retain[1]}
                                            >
                                                {(() => {
                                                    const vmIndex = transition.retain[0]*tilesPerRow + transition.retain[1];
                                                    return cs(
                                                        ["children", ({}, next) => Load({
                                                            fetch: () => getViewMoreItems(vmIndex),
                                                            next,
                                                        })],
                                                        ["child", ({children}, next) => children != null && children.map((value, i) => next({value, i}))],
                                                        ({child}, next) => <DelayRender {...{
                                                            delay: 300 + child.i * 100,
                                                            key: child.i,
                                                            next,
                                                        }}/>,
                                                        (_, next) => <CssTransition {...{
                                                            classNames: "from-right",
                                                            next,
                                                        }}/>,
                                                        ({child}) => (
                                                            <div
                                                                className="box"
                                                            >
                                                                {renderViewMoreItem(child.value, vmIndex)}
                                                            </div>
                                                        )
                                                    );
                                                })()}
                                            </div>
                                        )
                                    )}
                                </div>
                            </Fragment>
                        )
                    )}
                </Fragment>
            ))}
        </div>
    )
);

const getTileWidth = (mode, lg) => viewModes.find((m) => m.name == mode).tileWidth * (lg ? 1.3:1);
